import { useState, useEffect } from 'react';
import { db, storage } from '../../firebase/config';
import { collection, addDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Upload } from 'lucide-react';
import { Link } from 'react-router-dom';

const TeamForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEditMode);
    const [formData, setFormData] = useState({
        name: '',
        role: '',
        category: 'yonetim', // default
        image: ''
    });
    const [imageFile, setImageFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');

    useEffect(() => {
        if (isEditMode) {
            const fetchData = async () => {
                const docRef = doc(db, "team", id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setFormData(data);
                    setPreviewUrl(data.image);
                } else {
                    alert("Üye bulunamadı!");
                    navigate('/admin/team');
                }
                setFetching(false);
            };
            fetchData();
        }
    }, [id, isEditMode, navigate]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            let imageUrl = formData.image;

            if (imageFile) {
                const storageRef = ref(storage, `team/${Date.now()}_${imageFile.name}`);
                const snapshot = await uploadBytes(storageRef, imageFile);
                imageUrl = await getDownloadURL(snapshot.ref);
            }

            const dataToSave = {
                name: formData.name,
                role: formData.role,
                category: formData.category,
                image: imageUrl,
                updatedAt: new Date().toISOString()
            };

            if (isEditMode) {
                await updateDoc(doc(db, "team", id), dataToSave);
            } else {
                await addDoc(collection(db, "team"), {
                    ...dataToSave,
                    createdAt: new Date().toISOString()
                });
            }

            navigate('/admin/team');
        } catch (error) {
            console.error("Error saving member:", error);
            alert("Kaydetme sırasında bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    if (fetching) return <div className="text-white">Veri yükleniyor...</div>;

    return (
        <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
                <Link to="/admin/team" className="p-2 bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors">
                    <ArrowLeft size={20} />
                </Link>
                <h1 className="text-3xl font-bold text-white">
                    {isEditMode ? 'Üyeyi Düzenle' : 'Yeni Üye Ekle'}
                </h1>
            </div>

            <form onSubmit={handleSubmit} className="bg-gray-800 rounded-xl border border-gray-700 p-8 space-y-6">

                {/* Image Upload */}
                <div>
                    <label className="block text-gray-400 text-sm font-medium mb-2">Profil Fotoğrafı</label>

                    <div className="mb-4">
                        <label className="block text-gray-500 text-xs mb-1">Görsel URL (İnternetten kopyala yapıştır)</label>
                        <input
                            type="text"
                            value={formData.image || ''}
                            onChange={(e) => {
                                setFormData({ ...formData, image: e.target.value });
                                setPreviewUrl(e.target.value);
                            }}
                            className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none transition-colors"
                            placeholder="https://..."
                        />
                    </div>

                    <div className="flex items-center gap-4 mb-2">
                        <span className="text-gray-500 text-xs">VEYA Dosya Yükle:</span>
                    </div>

                    <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                        <div className="w-32 h-32 bg-gray-900 rounded-full border-2 border-dashed border-gray-700 flex flex-col items-center justify-center overflow-hidden relative group">
                            {previewUrl ? (
                                <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" onError={(e) => e.target.style.display = 'none'} />
                            ) : (
                                <div className="text-gray-500 flex flex-col items-center">
                                    <Upload size={20} className="mb-1" />
                                    <span className="text-[10px]">Fotoğraf</span>
                                </div>
                            )}
                            <input
                                type="file"
                                onChange={handleImageChange}
                                className="absolute inset-0 opacity-0 cursor-pointer"
                                accept="image/*"
                            />
                        </div>
                        <div className="flex-1 text-sm text-gray-500 py-4">
                            <p>Kare veya dikey format önerilir.</p>
                        </div>
                    </div>
                </div>

                {/* Name */}
                <div>
                    <label className="block text-gray-400 text-sm font-medium mb-2">Ad Soyad</label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none transition-colors"
                        placeholder="Örn: Ahmet Yılmaz"
                        required
                    />
                </div>

                {/* Role */}
                <div>
                    <label className="block text-gray-400 text-sm font-medium mb-2">Görev</label>
                    <input
                        type="text"
                        value={formData.role}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none transition-colors"
                        placeholder="Örn: Genel Sekreter / Üye"
                        required
                    />
                </div>

                {/* Category */}
                <div>
                    <label className="block text-gray-400 text-sm font-medium mb-2">Kategori</label>
                    <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none transition-colors"
                    >
                        <option value="baskan">Topluluk Başkanı</option>
                        <option value="yonetim">Yönetim Kurulu</option>
                        <option value="denetim">Denetim Kurulu</option>
                    </select>
                </div>

                {/* Submit */}
                <div className="pt-4 border-t border-gray-700 flex justify-end">
                    <button
                        type="submit"
                        disabled={loading}
                        className="bg-cyan-500 hover:bg-cyan-600 text-white px-8 py-3 rounded-lg font-bold flex items-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? 'Kaydediliyor...' : (
                            <>
                                <Save size={20} />
                                <span>Kaydet</span>
                            </>
                        )}
                    </button>
                </div>

            </form>
        </div>
    );
};

export default TeamForm;
